import { Loader2 } from 'lucide-react';
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label, Switch, useToast } from 'randmarcomps';
import { inject, observer } from 'mobx-react';
import React, { useState, useCallback } from 'react';
import { usePostV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberFieldNameMutation } from 'randmar-api-client';
import { useManufacturer } from '../../contexts/WithManufacturer';
import { RootStore } from '../../stores/rootStore';

interface IProps {
  store?: RootStore;
  informationName: string;
  informationCurrentValue?: string;
  label?: string;
  validateFonction: (string: string) => boolean;
  bidNumber: string;
  children: React.ReactNode;
}

const EditOpportunityWrapper: React.FC<IProps> = ({
  store,
  informationName,
  informationCurrentValue,
  label,
  bidNumber,
  children
}) => {
  const { applicationId } = useManufacturer();
  const [open, setOpen] = useState(false);
  const [informationValue, setInformationValue] = useState(informationCurrentValue || "");
  const { toast } = useToast();
  const [editOpportunity, { isLoading: loading }] = usePostV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberFieldNameMutation();

  const handleEditInformationClicked = useCallback(() => {
    setOpen(true);
  }, []);

  const handleConfirm = useCallback(async (propName: string, value: string) => {
    setOpen(false);

    let newValue = value;
    if (label === "Active") {
      newValue = value === "true" ? "false" : "true";
    }

    try {
      await editOpportunity({
        routeManufacturerId: applicationId,
        bidNumber,
        fieldName: propName,
        newValue
      }).unwrap();

      toast({ variant: "success", title: "Success", description: `Property ${propName} was successfully updated.` });
      store!.opportunityStore.getOpportunity(applicationId, bidNumber, true);
    } catch (err) {
      toast({ variant: "destructive", title: "Error", description: `Property ${propName} failed to update.` });
      console.error(err);
    }
  }, [store, applicationId, bidNumber, label, editOpportunity, toast]);

  const handleCancel = useCallback(() => {
    setOpen(false);
  }, []);

  const handleType = (infoName: string) => {
    if (infoName === "EndDate") return "date";
    if (infoName === "OpportunityName") return "text";
    return "";
  };

  const handleInformationChanged = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInformationValue(e.target.value);
  }, []);

  if (label === "Active") {
    return (
      <Switch
        checked={informationValue === "true"}
        onCheckedChange={() => handleConfirm(informationName, informationValue)}
      />
    );
  }

  return (
    <>
      <div onClick={handleEditInformationClicked}>
        {children}
      </div>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && !loading && handleCancel()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit {label || informationName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Label htmlFor={informationName}>{label || informationName}</Label>
            <Input
              autoFocus
              id={informationName}
              value={label === "EndDate" ? `${informationValue.substr(0, 4)}-${informationValue.substr(4, 2)}-${informationValue.substr(6, 2)}` : informationValue}
              onChange={handleInformationChanged}
              type={handleType(informationName)}
              disabled={loading}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={() => handleConfirm(informationName, informationValue)} disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default inject("store")(observer(EditOpportunityWrapper));
